/**
 * ONE icon + one color family per piece of equipment, everywhere (34i83,
 * Seth 2026-10-20: "use different icons like a condenser, evaporator coil,
 * furnace, boiler, filter, etc for specific pieces of equipment so it looks
 * nice … carry these across system and mobile"). The Property Timeline, the
 * address chips, the Equipment tab, Good to Know and the tech app's unit
 * lines all read this table, so a condenser is a fan and a coil a snowflake
 * on every screen.
 *
 *   furnace Flame · boiler / garage heater Heater · condenser Fan ·
 *   evaporator coil Snowflake · heat pump SunSnow · mini-split
 *   ThermometerSnowflake · air handler / ERV-HRV AirVent · rooftop Container ·
 *   Magic-Pak Box · water heater Cylinder · humidifier Droplets ·
 *   dehumidifier Haze · UV light Lightbulb · air cleaner Wind · thermostat
 *   Thermometer · filters Filter · RDS ShieldCheck · materials Wrench
 *
 * Families: heating warm (orange) · cooling cool (sky) · water (blue) ·
 * air quality (emerald) · controls and the rest (slate).
 */
import {
  AirVent, Box, Container, Cylinder, Droplets, Fan, Filter, Flame, Haze, Heater, Lightbulb,
  Package, ShieldCheck, Snowflake, SunSnow, Thermometer, ThermometerSnowflake, Wind, Wrench,
  type LucideIcon,
} from "lucide-react";
import type { EquipCategory } from "@/lib/equipment";

export type EquipFamily = "heating" | "cooling" | "water" | "air" | "control" | "other";

const ICON: Record<EquipCategory, LucideIcon> = {
  furnace: Flame,
  boiler: Heater,
  "garage-heater": Heater,
  ac: Fan,
  coil: Snowflake,
  "heat-pump": SunSnow,
  "mini-split": ThermometerSnowflake,
  "air-handler": AirVent,
  rooftop: Container,
  "magic-pak": Box,
  "water-heater": Cylinder,
  humidifier: Droplets,
  dehumidifier: Haze,
  uv: Lightbulb,
  "air-cleaner": Wind,
  "erv-hrv": AirVent,
  thermostat: Thermometer,
  rds: ShieldCheck,
  consumable: Filter,
  material: Wrench,
  other: Package,
};

const FAMILY: Record<EquipCategory, EquipFamily> = {
  furnace: "heating", boiler: "heating", "garage-heater": "heating",
  ac: "cooling", coil: "cooling", "heat-pump": "cooling", "mini-split": "cooling", "air-handler": "cooling", rooftop: "cooling", "magic-pak": "cooling",
  "water-heater": "water",
  humidifier: "air", dehumidifier: "air", uv: "air", "air-cleaner": "air", "erv-hrv": "air",
  thermostat: "control", rds: "control",
  consumable: "other", material: "other", other: "other",
};

/** Tailwind classes per family: the tinted tile (icon on a pale disc), the bare icon color, the timeline node. */
const TONE: Record<EquipFamily, { tile: string; ink: string; node: string }> = {
  heating: { tile: "bg-orange-50 text-orange-600", ink: "text-orange-500", node: "bg-orange-50 text-orange-600 ring-orange-100" },
  cooling: { tile: "bg-sky-50 text-sky-600", ink: "text-sky-500", node: "bg-sky-50 text-sky-600 ring-sky-100" },
  water: { tile: "bg-blue-50 text-blue-600", ink: "text-blue-500", node: "bg-blue-50 text-blue-600 ring-blue-100" },
  air: { tile: "bg-emerald-50 text-emerald-600", ink: "text-emerald-500", node: "bg-emerald-50 text-emerald-600 ring-emerald-100" },
  control: { tile: "bg-violet-50 text-violet-600", ink: "text-violet-500", node: "bg-violet-50 text-violet-600 ring-violet-100" },
  other: { tile: "bg-slate-100 text-slate-500", ink: "text-slate-400", node: "bg-slate-100 text-slate-500 ring-slate-100" },
};

export interface EquipStyle {
  Icon: LucideIcon;
  family: EquipFamily;
  /** Icon on a pale tinted disc / square. */
  tile: string;
  /** The bare icon's color on white. */
  ink: string;
  /** The 28px timeline node. */
  node: string;
}
export function equipStyle(cat: EquipCategory | string | null | undefined): EquipStyle {
  const c = (cat && cat in ICON ? cat : "other") as EquipCategory;
  const family = FAMILY[c];
  return { Icon: ICON[c], family, ...TONE[family] };
}
export function equipIcon(cat: EquipCategory | string | null | undefined): LucideIcon {
  return equipStyle(cat).Icon;
}
